"use client";

import type { JSX } from "react";
import type { Article, CategoryCounts, PaginatedArticles } from "@/lib/directus";
import { useState, Suspense } from "react";
import { CategoryFilter } from "@/components/blog/CategoryFilter";
import { ArticleListView } from "@/components/blog/ArticleListView";
import { Container } from "@/components/ui/Container";
import { Pagination } from "@/components/ui/pagination";

interface NutritionContentProps {
  initialArticles: Article[] | PaginatedArticles;
  categoryCounts?: CategoryCounts;
  pageTitle?: string;
  pageDescription?: string;
}

function PaginationWrapper({
  currentPage,
  totalPages,
  basePath,
}: {
  currentPage: number;
  totalPages: number;
  basePath: string;
}): JSX.Element {
  return (
    <Suspense fallback={<div className="h-10" />}>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        basePath={basePath}
        className="mt-12 sm:mt-16 md:mt-20"
      />
    </Suspense>
  );
}

export default function NutritionContent({
  initialArticles,
  categoryCounts,
  pageTitle = "Nutrition Articles",
  pageDescription = "Expert nutrition advice for a healthier lifestyle. Discover meal planning tips, macro tracking guidance, and evidence-based dietary strategies to support your fitness goals.",
}: NutritionContentProps): JSX.Element {
  const isPaginated = "total" in initialArticles;
  const articles = isPaginated ? initialArticles.articles : initialArticles;
  const paginationInfo = isPaginated
    ? {
        currentPage: initialArticles.page,
        totalPages: initialArticles.totalPages,
      }
    : null;

  const [filteredArticles, setFilteredArticles] =
    useState<Article[]>(articles);

  return (
    <div className="bg-white pt-8 pb-12 sm:pt-10 sm:pb-16 md:pt-12 md:pb-20">
      <Container size="lg" padding="md">
        <div className="w-full">
          <h1 className="text-pretty text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl md:text-6xl">
            {pageTitle}
          </h1>
          <p className="mt-2 text-base leading-7 text-gray-600 sm:text-lg sm:leading-8 md:mt-4 md:text-xl">
            {pageDescription}
          </p>
          <CategoryFilter
            articles={isPaginated ? undefined : articles}
            categoryCounts={categoryCounts}
            onFilter={isPaginated ? undefined : setFilteredArticles}
            initialCategory="Nutrition"
            useNavigation={isPaginated}
            basePath="/blog"
          />
          <ArticleListView
            articles={isPaginated ? articles : filteredArticles}
            clickSource="nutrition"
          />
          {paginationInfo && (
            <PaginationWrapper
              currentPage={paginationInfo.currentPage}
              totalPages={paginationInfo.totalPages}
              basePath="/nutrition"
            />
          )}
        </div>
      </Container>
    </div>
  );
}
